import {
    _generateNumericShortCodeFromShortCodeString,
    _generateShortCodeStringFromNumericShortCode,
} from "./ConvertNumericShortCodesToStrings.js";
import { EUI_STRING_LENGTH } from "./shortCodesLookup.js";
import { hasValidEuiFormat } from "./shortCodeValidation.js";

type InputShortCodeAddressRange = {
    name: string;
    startingEui: string;
    endingEui: string;
    relevantDeviceVersionSelector: string;
    startingShortCode: string;
    doLegacyRudeWordReplacement: boolean;
};

const inputShortCodeAddressRanges: InputShortCodeAddressRange[] = [
    {
        name: "Original EProm batch",
        relevantDeviceVersionSelector: "2,3.*.*.*.*",
        startingEui: "eui-0004a30b00e9f300",
        startingShortCode: "WAF-018",
        endingEui: "eui-0004a30b00eab700",
        doLegacyRudeWordReplacement: true,
    },
    {
        name: "Block gen 4 first batch of E-Proms",
        relevantDeviceVersionSelector: "4.*.*.*.*",
        // lowest known EUI: eui-fcc23d0000425ebc
        // highest known EUI: eui-fcc23d000043235a
        startingEui: "eui-fcc23d0000424000",
        endingEui: "eui-fcc23d0000433000",
        startingShortCode: "BAB-000",
        doLegacyRudeWordReplacement: false,
    },
    {
        name: "Block gen 4 second batch of E-Proms",
        relevantDeviceVersionSelector: "4.*.*.*.*",
        // lowest sampled EUI: eui-fcc23d000032ca28
        // highest sampled EUI: eui-fcc23d000036ec49
        startingEui: "eui-fcc23d0000320000",
        endingEui: "eui-fcc23d0000380000",
        startingShortCode: "ZOB-430",
        doLegacyRudeWordReplacement: false,
    },
];

type HydratedShortCodeAddressRange = {
    name: string;
    startingEui: string;
    relevantDeviceVersionSelector: string;
    startingShortCode: string;
    endingEui: string;
    doLegacyRudeWordReplacement: boolean;

    rangeSize: number;
    endingShortCode: string;

    startingEuiNumeric: bigint;
    endingEuiNumeric: bigint;

    startingShortCodeNumeric: bigint;
    endingShortCodeNumeric: bigint;
};

/**
 * Converts an EUI string to its numeric representation as a bigint.
 *
 * @internal This is an internal utility function.
 * @param eui - The EUI string to convert
 * @returns The numeric representation of the EUI as a bigint
 */
const euiToNumber: (eui: string) => bigint = (eui: string): bigint => BigInt(`0x${eui.replace("eui-", "")}`);

export const shortCodeAddressRanges: HydratedShortCodeAddressRange[] = inputShortCodeAddressRanges.map((inputRange) => {
    const totalRangeSize = euiToNumber(inputRange.endingEui) - euiToNumber(inputRange.startingEui);
    return {
        ...inputRange,
        startingEuiNumeric: euiToNumber(inputRange.startingEui),
        endingEuiNumeric: euiToNumber(inputRange.endingEui),

        startingShortCodeNumeric: BigInt(_generateNumericShortCodeFromShortCodeString(inputRange.startingShortCode)),
        endingShortCodeNumeric:
            BigInt(_generateNumericShortCodeFromShortCodeString(inputRange.startingShortCode)) + totalRangeSize,

        rangeSize: Number(totalRangeSize),
        endingShortCode: _generateShortCodeStringFromNumericShortCode(
            _generateNumericShortCodeFromShortCodeString(inputRange.startingShortCode) + Number(totalRangeSize),
            false,
        ),
    };
});

/**
 * Finds the address range that contains the given EUI.
 *
 * @param eui - The EUI string to find the range for
 * @returns The address range containing the EUI, or undefined if no range contains it
 */
export function getRelevantShortCodeAddressRangeFromEui(eui: string): HydratedShortCodeAddressRange | undefined {
    const numericEui = euiToNumber(eui);
    return shortCodeAddressRanges.find((range) => {
        if (numericEui >= range.startingEuiNumeric && numericEui <= range.endingEuiNumeric) {
            return true;
        }
        return false;
    });
}

/**
 * Validates that there are no conflicts between the defined address ranges.
 * Throws an error if any conflicts are found.
 *
 * @internal This function is called automatically when the module is loaded.
 */
function _checkForRangeConflicts() {
    const borderEuis: bigint[] = [];
    const borderEuiStrings: string[] = [];
    const borderShortCodes: bigint[] = [];
    shortCodeAddressRanges.forEach((range) => {
        borderEuis.push(range.startingEuiNumeric, range.endingEuiNumeric);
        borderShortCodes.push(range.startingShortCodeNumeric, range.endingShortCodeNumeric);
        borderEuiStrings.push(range.startingEui, range.endingEui);
    });

    borderEuis.forEach((eui) => {
        const matchingRanges = shortCodeAddressRanges.filter(
            (range) => range.startingEuiNumeric <= eui && range.endingEuiNumeric >= eui,
        );
        if (matchingRanges.length > 1) {
            const conflictingRangeNames = matchingRanges.map((r) => r.name).join(", ");
            const euiHex = `eui-${eui.toString(16).padStart(EUI_STRING_LENGTH, "0")}`;
            throw new Error(
                `Error with EUI ranges! The EUI ${euiHex} is included in multiple ranges: ${conflictingRangeNames}`,
            );
        }
    });

    borderEuiStrings.forEach((euiString) => {
        // Use the validation function from the dedicated module
        if (!hasValidEuiFormat(euiString)) {
            throw new Error(
                `Error with EUI ranges! The EUI "${euiString}" is invalid. EUIs must be 16 hexadecimal characters.`,
            );
        }
    });

    borderShortCodes.forEach((numericShortCode) => {
        const matchingRanges = shortCodeAddressRanges.filter(
            (range) =>
                range.startingShortCodeNumeric <= numericShortCode && range.endingShortCodeNumeric >= numericShortCode,
        );
        if (matchingRanges.length > 1) {
            // Convert numeric short code to string for better error message
            const shortCodeString = _generateShortCodeStringFromNumericShortCode(Number(numericShortCode), false);
            const conflictingRangeNames = matchingRanges.map((r) => r.name).join(", ");
            throw new Error(
                `Error with Short code ranges! The short code ${shortCodeString} (numeric: ${numericShortCode}) is included in multiple ranges: ${conflictingRangeNames}`,
            );
        }
    });
}

_checkForRangeConflicts();
